import { Head, useForm, usePage } from "@inertiajs/react";
import type { InertiaFormProps } from "@inertiajs/react";
import {
  Building,
  Calendar,
  DollarSign,
  PackagePlus,
  Plus,
  Send,
  Trash2,
} from "lucide-react";
import { useState } from "react";

import AsyncCombobox from "@/components/async-combobox";
import { SearchableSelect } from "@/components/searchable-select";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { TextareaWithCounter } from "@/components/ui/textarea-with-counter";
import { fetchProductOptions } from "@/helpers/product-search";
import { useMediaQuery } from "@/hooks/use-media-query";
import { useOrganizationRoutes } from "@/hooks/use-organization-routes";
import type { SharedData } from "@/types";
import { getTodayDateString } from "@/utils/date";

interface Sector {
  id: number;
  name: string;
}

interface SimpleNewProps {
  sectors: Sector[];
}

interface SimpleItem {
  product_id: number | null;
  product_name: string | null;
  quantity: number;
  unit: string;
}

interface UnitOption {
  label: string;
  value: string;
}

interface AddItemFormData {
  product_id: number | null;
  product_name: string | null;
  quantity: number;
  unit: string;
}

interface AddItemFormProps {
  form: InertiaFormProps<AddItemFormData>;
  unitOptions: UnitOption[];
  organizationSlug: string;
  className?: string;
  inDrawer?: boolean;
}

function AddItemForm({
  form,
  unitOptions,
  organizationSlug,
  className,
  inDrawer = false,
}: AddItemFormProps) {
  const selectedProduct =
    form.data.product_id && form.data.product_name
      ? { value: form.data.product_id, label: form.data.product_name }
      : null;

  return (
    <div className={className}>
      <div className="space-y-4">
        {/* Producto */}
        <div className="space-y-2">
          <Label>Producto *</Label>
          <AsyncCombobox
            value={selectedProduct}
            onChange={(opt) => {
              form.setData({
                ...form.data,
                product_id: opt ? Number(opt.value) : null,
                product_name: opt?.label ?? null,
              });
            }}
            loadOptions={(term) => fetchProductOptions(term, organizationSlug)}
            placeholder="Buscar producto..."
          />
        </div>

        {/* Cantidad y Unidad */}
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Cantidad *</Label>
            <Input
              type="number"
              inputMode="numeric"
              step="0.01"
              min="0"
              value={form.data.quantity}
              onChange={(e) => form.setData("quantity", Number(e.target.value))}
            />
          </div>
          <div className="space-y-2">
            <Label>Unidad *</Label>
            <Select
              value={form.data.unit}
              onValueChange={(val) => form.setData("unit", val)}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Unidad" />
              </SelectTrigger>
              <SelectContent
                position={inDrawer ? "popper" : undefined}
                sideOffset={5}
              >
                {unitOptions.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function SimpleNew({ sectors }: SimpleNewProps) {
  const shared = usePage<SharedData>().props;
  const routes = useOrganizationRoutes();
  const quickCreateHref = routes.purchaseRequests.quickCreate();
  const unitOptions: UnitOption[] = shared.enums?.units ?? [
    { label: "UN - Unidades", value: "UN" },
  ];
  const defaultUnit = unitOptions[0]?.value ?? "UN";

  const isDesktop = useMediaQuery("(min-width: 768px)");

  const { data, setData, post, processing, transform } = useForm<{
    quick_request: {
      sector_id: number | null;
      needed_by: string | null;
      estimated_total: number | null;
      general_observation: string | null;
      items: {
        raw_description: string;
        quantity: number;
        unit: string;
        product_id: number | null;
      }[];
    };
  }>({
    quick_request: {
      sector_id: null,
      needed_by: null,
      estimated_total: null,
      general_observation: null,
      items: [],
    },
  });

  // Dialog/Drawer state
  const [addItemDialogOpen, setAddItemDialogOpen] = useState(false);

  // Form for adding new item using Inertia's useForm
  const addItemForm = useForm<AddItemFormData>({
    product_id: null,
    product_name: null,
    quantity: 1,
    unit: defaultUnit,
  });

  // Temporary list to visualize items before submission
  const [items, setItems] = useState<SimpleItem[]>([]);

  // Validation errors
  const [validationError, setValidationError] = useState<string | null>(null);

  const handleAddItem = () => {
    if (!addItemForm.data.product_id || !addItemForm.data.product_name) return;
    const qty = addItemForm.data.quantity;
    if (isNaN(qty) || qty <= 0 || !addItemForm.data.unit) return;

    const newItem: SimpleItem = {
      product_id: addItemForm.data.product_id,
      product_name: addItemForm.data.product_name,
      quantity: addItemForm.data.quantity,
      unit: addItemForm.data.unit,
    };

    setItems([...items, newItem]);
    addItemForm.reset();
    setAddItemDialogOpen(false);
  };

  const handleRemoveItem = (index: number) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const handleSubmit = () => {
    // Validate required fields
    if (!data.quick_request.sector_id) {
      setValidationError("El sector es obligatorio");
      return;
    }
    if (!data.quick_request.needed_by) {
      setValidationError("La fecha de necesidad es obligatoria");
      return;
    }
    if (items.length === 0) {
      setValidationError("Debes agregar al menos un producto");
      return;
    }

    setValidationError(null);

    // Transform items to backend format
    transform(() => ({
      quick_request: {
        sector_id: data.quick_request.sector_id,
        needed_by: data.quick_request.needed_by,
        estimated_total: data.quick_request.estimated_total,
        general_observation: data.quick_request.general_observation,
        items: items.map((it) => ({
          raw_description: it.product_name,
          quantity: it.quantity,
          unit: it.unit,
          product_id: it.product_id,
        })),
      },
    }));
    post(quickCreateHref);
  };

  return (
    <>
      <Head title="Nueva Solicitud Simple" />

      {/* Sin AppLayout - fullscreen simple */}
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
        {/* Header simple */}
        <div className="sticky top-0 z-10 border-b bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/80">
          <div className="flex items-center justify-between px-4 py-3">
            <div>
              <h1 className="text-xl font-semibold text-slate-900">
                Nueva Solicitud
              </h1>
              <p className="text-xs text-slate-600">Crea un pedido rápido</p>
            </div>
            <Button
              size="sm"
              onClick={handleSubmit}
              disabled={processing}
              data-testid="purchase-request-submit"
            >
              {processing ? (
                "Enviando..."
              ) : (
                <>
                  <Send className="h-4 w-4 mr-1" /> Enviar
                </>
              )}
            </Button>
          </div>
          {validationError && (
            <div className="px-4 pb-3">
              <div
                className="rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-800"
                role="alert"
              >
                {validationError}
              </div>
            </div>
          )}
        </div>

        {/* Content */}
        <div className="max-w-2xl mx-auto p-4 space-y-4">
          {/* Datos de la solicitud */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Datos de la solicitud</CardTitle>
              <CardDescription className="text-xs">
                Información básica del pedido
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Sector */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2 text-sm">
                  <Building className="h-4 w-4" /> Obra/Sector *
                </Label>
                <SearchableSelect
                  options={sectors.map((s) => ({
                    value: String(s.id),
                    label: s.name,
                  }))}
                  value={
                    data.quick_request.sector_id
                      ? String(data.quick_request.sector_id)
                      : ""
                  }
                  onValueChange={(val) =>
                    setData("quick_request", {
                      ...data.quick_request,
                      sector_id: val ? Number(val) : null,
                    })
                  }
                  placeholder="Seleccionar sector"
                />
              </div>

              {/* Fecha de necesidad */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2 text-sm">
                  <Calendar className="h-4 w-4" /> Fecha de necesidad *
                </Label>
                <Input
                  type="date"
                  min={getTodayDateString()}
                  value={data.quick_request.needed_by ?? ""}
                  onChange={(e) =>
                    setData("quick_request", {
                      ...data.quick_request,
                      needed_by: e.target.value === "" ? null : e.target.value,
                    })
                  }
                  data-testid="needed-by-date"
                />
              </div>

              {/* Estimado de gasto */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2 text-sm">
                  <DollarSign className="h-4 w-4" /> Estimado de gasto
                </Label>
                <div className="relative">
                  <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-slate-500">
                    $
                  </span>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                    className="pl-8"
                    value={data.quick_request.estimated_total ?? ""}
                    onChange={(e) => {
                      const numValue = parseFloat(e.target.value);
                      setData("quick_request", {
                        ...data.quick_request,
                        estimated_total:
                          e.target.value === "" || isNaN(numValue)
                            ? null
                            : numValue,
                      });
                    }}
                  />
                </div>
              </div>

              {/* Observaciones */}
              <div className="space-y-2">
                <Label className="text-sm">Observaciones</Label>
                <TextareaWithCounter
                  rows={3}
                  placeholder="Comentarios adicionales..."
                  value={data.quick_request.general_observation ?? ""}
                  onChange={(e) =>
                    setData("quick_request", {
                      ...data.quick_request,
                      general_observation:
                        e.target.value === "" ? null : e.target.value,
                    })
                  }
                  maxLength={500}
                  showCharacterCount={true}
                />
              </div>
            </CardContent>
          </Card>

          {/* Productos */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-base">Productos</CardTitle>
                  <CardDescription className="text-xs">
                    {items.length} producto{items.length !== 1 ? "s" : ""}
                  </CardDescription>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setAddItemDialogOpen(true)}
                  data-testid="add-product-button"
                >
                  <Plus className="h-4 w-4 mr-1" /> Agregar
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {items.length === 0 ? (
                <div className="text-center py-8">
                  <PackagePlus className="h-12 w-12 text-slate-300 mx-auto mb-2" />
                  <p className="text-sm text-slate-500">
                    No hay productos agregados
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-3"
                    onClick={() => setAddItemDialogOpen(true)}
                  >
                    <Plus className="h-4 w-4 mr-1" /> Agregar primer producto
                  </Button>
                </div>
              ) : (
                <div className="space-y-2">
                  {items.map((item, idx) => (
                    <div
                      key={idx}
                      className="flex items-start justify-between gap-3 rounded-lg border bg-slate-50/50 p-3"
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-900 truncate">
                          {item.product_name}
                        </p>
                        <p className="text-xs text-slate-600 mt-0.5">
                          {item.quantity} {item.unit}
                        </p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveItem(idx)}
                        className="h-8 w-8 p-0 shrink-0"
                        aria-label="Eliminar producto"
                      >
                        <Trash2 className="h-4 w-4 text-slate-500" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Bottom spacer for mobile */}
          <div className="h-20" />
        </div>
      </div>

      {/* Responsive Dialog/Drawer para agregar producto */}
      {isDesktop ? (
        <Dialog
          open={addItemDialogOpen}
          onOpenChange={(open) => {
            if (!open) {
              addItemForm.reset();
            }
            setAddItemDialogOpen(open);
          }}
        >
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Agregar producto</DialogTitle>
              <DialogDescription>
                Selecciona un producto, cantidad y unidad
              </DialogDescription>
            </DialogHeader>

            <AddItemForm
              form={addItemForm}
              unitOptions={unitOptions}
              organizationSlug={routes.organizationSlug}
            />

            {/* Acciones */}
            <div className="flex justify-end gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  addItemForm.reset();
                  setAddItemDialogOpen(false);
                }}
              >
                Cancelar
              </Button>
              <Button
                onClick={handleAddItem}
                disabled={
                  !addItemForm.data.product_id ||
                  !addItemForm.data.product_name ||
                  addItemForm.data.quantity <= 0 ||
                  !addItemForm.data.unit
                }
                data-testid="dialog-add-product-confirm"
              >
                Agregar
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      ) : (
        <Drawer
          open={addItemDialogOpen}
          onOpenChange={(open) => {
            if (!open) {
              addItemForm.reset();
            }
            setAddItemDialogOpen(open);
          }}
        >
          <DrawerContent className="max-h-[85vh]">
            <DrawerHeader className="text-left">
              <DrawerTitle>Agregar producto</DrawerTitle>
              <DrawerDescription>
                Selecciona un producto, cantidad y unidad
              </DrawerDescription>
            </DrawerHeader>

            <div className="overflow-y-auto px-4">
              <AddItemForm
                form={addItemForm}
                unitOptions={unitOptions}
                organizationSlug={routes.organizationSlug}
                inDrawer
              />
            </div>

            <DrawerFooter className="pt-2">
              <Button
                onClick={handleAddItem}
                disabled={
                  !addItemForm.data.product_id ||
                  !addItemForm.data.product_name ||
                  addItemForm.data.quantity <= 0 ||
                  !addItemForm.data.unit
                }
                data-testid="dialog-add-product-confirm"
              >
                Agregar
              </Button>
              <DrawerClose asChild>
                <Button
                  variant="outline"
                  onClick={() => {
                    addItemForm.reset();
                  }}
                >
                  Cancelar
                </Button>
              </DrawerClose>
            </DrawerFooter>
          </DrawerContent>
        </Drawer>
      )}
    </>
  );
}
